import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FaQuestionCircle, FaUserGraduate, FaStar, FaClock, FaThumbsUp, FaComment, FaSearch } from 'react-icons/fa';

interface Question {
  id: string;
  title: string;
  content: string;
  author: {
    name: string;
    avatar: string;
  };
  teacher?: {
    name: string;
    avatar: string;
    rating: number;
    country?: string;
    yearsTeaching?: number;
    hourlyRate?: number;
    currency?: string;
    specializations?: string[];
  };
  answer?: string;
  tags: string[];
  createdAt: string;
  likes: number;
  comments: number;
  isAnswered: boolean;
  category: string;
}

const TeacherQA: React.FC = () => {
  const [questions] = useState<Question[]>([
    {
      id: '1',
      title: 'How do I properly use "ta\'arof" in Persian conversations?',
      content: 'I\'ve been learning Persian for a few months and I\'m confused about when and how to use ta\'arof. Can someone explain the cultural context and give me some practical examples?',
      author: {
        name: 'Sarah Johnson',
        avatar: 'https://randomuser.me/api/portraits/women/44.jpg'
      },
      teacher: {
        name: 'Negin Nickparsa',
        avatar: '/images/profiles/negin-nickparsa.jpg',
        rating: 4.9,
        country: 'Canada',
        yearsTeaching: 6,
        hourlyRate: 28,
        currency: 'USD',
        specializations: ['Early Childhood Persian', 'Family Language Learning', 'Multilingual Education']
      },
      answer: 'Ta\'arof is a beautiful aspect of Persian culture that shows politeness and respect. Here are some common examples:\n\n1. When offered food/drink: "Nay, merci" (No, thank you) - even if you want it\n2. When complimented: "خیلی لطف دارید" (You\'re very kind)\n3. When someone offers to pay: "Nay, man midam" (No, I\'ll pay)\n\nThe key is to refuse once or twice before accepting, showing humility.',
      tags: ['ta\'arof', 'culture', 'politeness', 'beginner'],
      createdAt: '2024-01-15T10:30:00Z',
      likes: 24,
      comments: 8,
      isAnswered: true,
      category: 'Culture'
    },
    {
      id: '2',
      title: 'What\'s the difference between "khub" and "beh"?',
      content: 'I see both words used to mean "good" but I\'m not sure when to use which one. Can someone clarify the difference?',
      author: {
        name: 'Michael Chen',
        avatar: 'https://randomuser.me/api/portraits/men/32.jpg'
      },
      teacher: {
        name: 'Negin Nickparsa',
        avatar: '/images/profiles/negin-nickparsa.jpg',
        rating: 4.9,
        country: 'Canada',
        yearsTeaching: 6,
        hourlyRate: 28,
        currency: 'USD',
        specializations: ['Early Childhood Persian', 'Family Language Learning', 'Multilingual Education']
      },
      answer: 'Great question! Here\'s the difference:\n\n"Khub" (خوب) is more general and means "good" in a broad sense.\n"Beh" (به) is more specific and often means "better" or "preferable."\n\nExamples:\n- "In film khub bud" (This movie was good)\n- "Beh in ke un" (This is better than that)\n\n"Beh" is also used in comparative structures.',
      tags: ['vocabulary', 'grammar', 'beginner'],
      createdAt: '2024-01-14T15:45:00Z',
      likes: 18,
      comments: 5,
      isAnswered: true,
      category: 'Grammar'
    },
    {
      id: '3',
      title: 'How do I conjugate verbs in present tense?',
      content: 'I\'m struggling with Persian verb conjugation. Can someone explain the pattern for present tense verbs?',
      author: {
        name: 'Emma Rodriguez',
        avatar: 'https://randomuser.me/api/portraits/women/56.jpg'
      },
      tags: ['grammar', 'verbs', 'conjugation', 'beginner'],
      createdAt: '2024-01-13T09:20:00Z',
      likes: 12,
      comments: 3,
      isAnswered: false,
      category: 'Grammar'
    },
    {
      id: '4',
      title: 'Best resources for learning Persian calligraphy?',
      content: 'I want to learn Persian calligraphy. What are the best resources, books, or online courses for beginners?',
      author: {
        name: 'David Kim',
        avatar: 'https://randomuser.me/api/portraits/men/67.jpg'
      },
      teacher: {
        name: 'Negin Nickparsa',
        avatar: '/images/profiles/negin-nickparsa.jpg',
        rating: 4.9,
        country: 'Canada',
        yearsTeaching: 6,
        hourlyRate: 28,
        currency: 'USD',
        specializations: ['Early Childhood Persian', 'Family Language Learning', 'Multilingual Education']
      },
      answer: 'For Persian calligraphy, I recommend:\n\n1. Start with "Nastaliq" style - it\'s the most common\n2. Practice basic strokes and letter forms\n3. Use quality paper and pens\n4. Study the works of master calligraphers\n\nOnline resources:\n- Persian Calligraphy YouTube channels\n- Practice sheets from calligraphy websites\n- Join Persian calligraphy groups on social media',
      tags: ['calligraphy', 'art', 'resources', 'intermediate'],
      createdAt: '2024-01-12T14:15:00Z',
      likes: 31,
      comments: 12,
      isAnswered: true,
      category: 'Art & Culture'
    },
    {
      id: '5',
      title: 'How to pronounce "gh" sound correctly?',
      content: 'I\'m having trouble with the "gh" sound in Persian. It sounds different from English "g". Any tips?',
      author: {
        name: 'Lisa Thompson',
        avatar: 'https://randomuser.me/api/portraits/women/23.jpg'
      },
      tags: ['pronunciation', 'phonetics', 'beginner'],
      createdAt: '2024-01-11T11:30:00Z',
      likes: 8,
      comments: 2,
      isAnswered: false,
      category: 'Pronunciation'
    }
  ]);

  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState<string>('');

  const categories = [
    { id: 'all', name: 'All Questions', count: questions.length },
    { id: 'grammar', name: 'Grammar', count: questions.filter(q => q.category === 'Grammar').length },
    { id: 'pronunciation', name: 'Pronunciation', count: questions.filter(q => q.category === 'Pronunciation').length },
    { id: 'culture', name: 'Culture', count: questions.filter(q => q.category === 'Culture').length },
    { id: 'vocabulary', name: 'Vocabulary', count: questions.filter(q => q.category === 'Vocabulary').length },
    { id: 'art', name: 'Art & Culture', count: questions.filter(q => q.category === 'Art & Culture').length }
  ];

  const filteredQuestions = questions.filter(question => {
    const matchesCategory = selectedCategory === 'all' || question.category.toLowerCase() === selectedCategory;
    const matchesSearch = question.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         question.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         question.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesCategory && matchesSearch;
  });

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours} hours ago`;
    if (diffInHours < 48) return 'Yesterday';
    return date.toLocaleDateString();
  };

  // Helper function to render text with proper RTL support for Persian
  const renderTextWithRTL = (text: string) => {
    // Split text by lines to handle mixed content
    const lines = text.split('\n');
    return lines.map((line, index) => {
      // Check if line contains Persian characters
      const hasPersian = /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/.test(line);
      
      if (hasPersian) {
        return (
          <div key={index} className="text-right" dir="rtl" style={{fontFamily: 'Vazirmatn, serif'}}>
            {line}
          </div>
        );
      } else {
        return (
          <div key={index} className="text-left">
            {line}
          </div>
        );
      }
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-orange-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
        {/* Header */}
        <div className="text-center mb-8 sm:mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-r from-orange-400 to-red-500 rounded-full mb-4 sm:mb-6 shadow-lg">
            <FaUserGraduate className="text-white text-2xl sm:text-3xl" />
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-800 mb-3 sm:mb-4 bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent">
            Teacher Q&A
          </h1>
          <p className="text-base sm:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed px-2">
            Get answers to your Persian language questions from our expert teachers. 
            Ask anything about grammar, pronunciation, culture, or vocabulary!
          </p>
        </div>

        {/* Ask Question Button */}
        <div className="text-center mb-8 sm:mb-10">
          <Link
            to="/forum/ask"
            className="inline-flex items-center px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-orange-500 to-red-500 text-white font-semibold rounded-xl hover:from-orange-600 hover:to-red-600 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1 text-sm sm:text-base"
          >
            <FaQuestionCircle className="mr-2 sm:mr-3 text-base sm:text-lg" />
            Ask a Question
          </Link>
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-6 lg:p-8 mb-6 sm:mb-8 border border-gray-100">
          <div className="flex flex-col gap-4 sm:gap-6">
            {/* Search */}
            <div className="relative">
              <FaSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search questions, topics, or keywords..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200 text-gray-700 placeholder-gray-400 text-sm sm:text-base"
              />
            </div>
            
            {/* Category Filter */}
            <div className="flex flex-wrap gap-2 sm:gap-3">
              {categories.map(category => (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`px-3 sm:px-4 py-2 rounded-xl text-xs sm:text-sm font-medium transition-all duration-200 ${
                    selectedCategory === category.id
                      ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white shadow-md'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200 hover:shadow-md'
                  }`}
                >
                  {category.name} ({category.count})
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Questions List */}
        <div className="space-y-4 sm:space-y-6">
          {filteredQuestions.map(question => (
            <div key={question.id} className="bg-white rounded-2xl shadow-lg p-4 sm:p-6 lg:p-8 border border-gray-100 hover:shadow-xl transition-all duration-300">
              <div className="flex flex-col sm:flex-row sm:items-start space-y-4 sm:space-y-0 sm:space-x-6">
                {/* Author Avatar */}
                <div className="flex-shrink-0 flex justify-center sm:justify-start">
                  <img
                    src={question.author.avatar}
                    alt={question.author.name}
                    className="w-12 h-12 sm:w-16 sm:h-16 rounded-full border-4 border-orange-100 shadow-md"
                  />
                </div>
                
                <div className="flex-1 min-w-0">
                  {/* Question Header */}
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 space-y-2 sm:space-y-0">
                    <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-3">
                      <span className="font-semibold text-gray-800 text-base sm:text-lg">
                        {question.author.name}
                      </span>
                      <div className="flex flex-wrap gap-2">
                        <span className="text-xs sm:text-sm text-gray-500 bg-gray-100 px-2 sm:px-3 py-1 rounded-full">
                          {formatDate(question.createdAt)}
                        </span>
                        {question.isAnswered && (
                          <span className="px-2 sm:px-3 py-1 bg-green-100 text-green-700 text-xs sm:text-sm font-medium rounded-full flex items-center">
                            <FaStar className="w-3 h-3 mr-1 text-green-500" />
                            Answered
                          </span>
                        )}
                      </div>
                    </div>
                    <span className="text-xs sm:text-sm text-gray-500 bg-orange-100 text-orange-700 px-2 sm:px-3 py-1 rounded-full font-medium self-start sm:self-auto">
                      {question.category}
                    </span>
                  </div>

                  {/* Question Title */}
                  <h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-3 leading-tight">
                    {question.title}
                  </h3>

                  {/* Question Content */}
                  <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6 leading-relaxed">
                    {question.content}
                  </p>

                  {/* Tags */}
                  <div className="flex flex-wrap gap-2 mb-4 sm:mb-6">
                    {question.tags.map(tag => (
                      <span
                        key={tag}
                        className="px-2 sm:px-3 py-1 bg-gradient-to-r from-orange-100 to-red-100 text-orange-700 text-xs sm:text-sm rounded-full font-medium border border-orange-200"
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>

                  {/* Answer Section */}
                  {question.isAnswered && question.teacher && question.answer && (
                    <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 rounded-2xl p-4 sm:p-6 mb-4 sm:mb-6">
                      <div className="flex flex-col sm:flex-row sm:items-center mb-4 space-y-3 sm:space-y-0">
                        <div className="relative flex justify-center sm:justify-start">
                          <img
                            src={question.teacher.avatar}
                            alt={question.teacher.name}
                            className="w-10 h-10 sm:w-12 sm:h-12 rounded-full border-3 border-green-200"
                          />
                          <div className="absolute -bottom-1 -right-1 w-5 h-5 sm:w-6 sm:h-6 bg-green-500 rounded-full flex items-center justify-center">
                            <FaStar className="text-white text-xs" />
                          </div>
                        </div>
                        <div className="sm:ml-4 text-center sm:text-left">
                          <div className="flex flex-col sm:flex-row sm:items-center space-y-1 sm:space-y-0">
                            <span className="font-bold text-gray-800 text-base sm:text-lg">
                              {question.teacher.name}
                            </span>
                            <div className="flex items-center justify-center sm:justify-start sm:ml-3">
                              <FaStar className="text-yellow-400 w-4 h-4" />
                              <span className="text-xs sm:text-sm text-gray-600 ml-1 font-medium">
                                {question.teacher.rating}
                              </span>
                            </div>
                          </div>
                          <span className="text-xs sm:text-sm text-green-600 font-medium">
                            Expert Teacher Answer
                          </span>
                          {/* Enhanced Teacher Profile Info */}
                          {question.teacher.country && (
                            <div className="mt-2 flex flex-wrap gap-2">
                              <span className="text-xs text-gray-600 bg-gray-100 px-2 py-1 rounded-full">
                                📍 {question.teacher.country}
                              </span>
                              {question.teacher.yearsTeaching && (
                                <span className="text-xs text-gray-600 bg-gray-100 px-2 py-1 rounded-full">
                                  🎓 {question.teacher.yearsTeaching} years teaching
                                </span>
                              )}
                              {question.teacher.hourlyRate && (
                                <span className="text-xs text-gray-600 bg-gray-100 px-2 py-1 rounded-full">
                                  💰 ${question.teacher.hourlyRate}/{question.teacher.currency || 'USD'}
                                </span>
                              )}
                            </div>
                          )}
                          {question.teacher.specializations && question.teacher.specializations.length > 0 && (
                            <div className="mt-2">
                              <div className="text-xs text-gray-500 mb-1">Specializations:</div>
                              <div className="flex flex-wrap gap-1">
                                {question.teacher.specializations.slice(0, 3).map((spec, index) => (
                                  <span key={index} className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded-full border border-blue-200">
                                    {spec}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="bg-white rounded-xl p-3 sm:p-4 border border-green-100">
                        <div className="text-sm sm:text-base text-gray-700 leading-relaxed">
                          {renderTextWithRTL(question.answer)}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Stats */}
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between text-xs sm:text-sm text-gray-500 pt-4 border-t border-gray-100 space-y-3 sm:space-y-0">
                    <div className="flex flex-wrap items-center gap-3 sm:gap-6">
                      <div className="flex items-center bg-gray-50 px-2 sm:px-3 py-1 sm:py-2 rounded-lg">
                        <FaThumbsUp className="mr-1 sm:mr-2 text-orange-500 w-3 h-3 sm:w-4 sm:h-4" />
                        <span className="font-medium">{question.likes}</span>
                      </div>
                      <div className="flex items-center bg-gray-50 px-2 sm:px-3 py-1 sm:py-2 rounded-lg">
                        <FaComment className="mr-1 sm:mr-2 text-blue-500 w-3 h-3 sm:w-4 sm:h-4" />
                        <span className="font-medium">{question.comments}</span>
                      </div>
                    </div>
                    <div className="flex items-center bg-gray-50 px-2 sm:px-3 py-1 sm:py-2 rounded-lg self-start sm:self-auto">
                      <FaClock className="mr-1 sm:mr-2 text-gray-400 w-3 h-3 sm:w-4 sm:h-4" />
                      <span className="font-medium">{formatDate(question.createdAt)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Empty State */}
        {filteredQuestions.length === 0 && (
          <div className="text-center py-12 sm:py-16 bg-white rounded-2xl shadow-lg border border-gray-100">
            <div className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 bg-gray-100 rounded-full mb-4 sm:mb-6">
              <FaQuestionCircle className="text-gray-400 text-2xl sm:text-3xl" />
            </div>
            <h3 className="text-xl sm:text-2xl font-bold text-gray-800 mb-3">
              No questions found
            </h3>
            <p className="text-sm sm:text-base text-gray-600 mb-6 sm:mb-8 max-w-md mx-auto px-4">
              Try adjusting your search or filter criteria to find what you're looking for.
            </p>
            <Link
              to="/forum/ask"
              className="inline-flex items-center px-4 sm:px-6 py-2 sm:py-3 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-xl hover:from-orange-600 hover:to-red-600 transition-all duration-300 shadow-lg hover:shadow-xl text-sm sm:text-base"
            >
              <FaQuestionCircle className="mr-2 w-3 h-3 sm:w-4 sm:h-4" />
              Ask the First Question
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default TeacherQA; 